import React, { Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import useRouteBreadcrumbs from './hooks/useRouteBreadcrumbs';

// Load immediately (not lazy)
import DashboardLayout from './layouts/DashboardLayout';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login/Login';

// Import lazy loaded components
import {
  Settings,
  Signup,
  Reports,
  GraphicalReports,
  ReportHistory,
  ReportTemplate,
  Profile,
  Logs,
  MyTemplates,
  Inbox,
  PrivateRoute,
  DashboardOverview,
  WorkflowLibraryPage,
  IntegrationsLibraryPage,
  DetailViewPage,
  WorkflowBuilder,
  WorkflowSchedule,
  ForgotPassword,
  SetNewPassword,
  ExpRunmybotPage,
  SocialMediaCallback,
  Partner,
  PageNotFound,
  Customer,
  Users,
  Teams,
  EmailVerificationPage,
  PartnerDashboard,
  AccessDenied,
  Calendar,
  Schedule,
  AuditLog,
  OAuthCallbackPage,
  ConnectionsList,
  AuthenticationConfig,
  UnifiedWorkflowList,
  UnifiedAPI,
  UnifiedSchedule,
  UnifiedCalendar,
  UnifiedConnection,
  Unified403,
  InvalidPage
} from './LazyComponents';

// Loading component
const LoadingFallback = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      width: '100%'
    }}
  >
    <CircularProgress size={60} />
  </Box>
);

const AppRoutes = () => {
  useRouteBreadcrumbs();

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/verify-email/:code/:token" element={<EmailVerificationPage />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/setnew-password/:userId/:token" element={<SetNewPassword />} />
        <Route path="/accounts/social/google/login/callback" element={<SocialMediaCallback />} />
        <Route path="/accounts/social/microsoft/login/callback" element={<SocialMediaCallback />} />
        <Route path="/accounts/social/linkedin/login/callback" element={<SocialMediaCallback />} />
        <Route path="/oauth/callback" element={<OAuthCallbackPage />} />

        <Route path="/workflow-list/:slug?/:system?" element={<UnifiedWorkflowList />} />
        <Route path="/invalid-page" element={<InvalidPage />} />
        <Route path="/workflow-detail/:id?" element={<UnifiedAPI />} />
        <Route path="/schedule/:name?" element={<UnifiedSchedule />} />
        <Route path="/calendar/:name?" element={<UnifiedCalendar />} />
        <Route path="/unified-connection/:id?" element={<UnifiedConnection />} />
        <Route path="/access-denied" element={<Unified403 />} />

        {/* Protected Routes */}
        <Route element={<PrivateRoute />}>
          <Route path="/" element={<DashboardLayout />}>
            <Route path="user-management/partner" element={<Partner />} />
            <Route path="user-management/customer" element={<Customer />} />
            <Route path="user-management/users/:id?" element={<Users />} />
            <Route path="user-management/teams" element={<Teams />} />
            <Route path="choicescreen" index element={<DashboardOverview />} />
            <Route path="dashboard" index element={<Dashboard />} />
            <Route path="partner-dashboard" index element={<PartnerDashboard />} />
            <Route path="integrations" index element={<IntegrationsLibraryPage />} />
            <Route path="workflow-library" index element={<WorkflowLibraryPage />} />
            <Route path="exp-runmybot" index element={<ExpRunmybotPage />} />
            <Route path="event-pro/calendar/:name?" index element={<Calendar />} />
            <Route path="event-pro/schedule/:name?" index element={<Schedule />} />
            <Route path="my-templates" index element={<MyTemplates />} />
            <Route path="connections" index element={<ConnectionsList />} />
            <Route path="connections/:id?/:type?" index element={<AuthenticationConfig />} />
            <Route path="inbox/:notificationId?" index element={<Inbox />} />
            <Route path="logs/:id?" index element={<Logs />} />
            <Route path="audit-log/:id?" index element={<AuditLog />} />
            <Route path="profile" element={<Profile />} />
            <Route path="insight/:type/:id?/:name?" element={<Reports />} />
            <Route path="report-dashboard" element={<GraphicalReports />} />
            <Route path="report-templates" element={<ReportTemplate />} />
            <Route path="report-history/:id?" element={<ReportHistory />} />
            <Route path="settings/:id?" element={<Settings />} />
            <Route path="process-flow/:id/:status?/:type?" element={<DetailViewPage />} />
            <Route path="workflow-builder/:type?/:id?" element={<WorkflowBuilder />} />
            <Route path="workflow-schedule/:id" element={<WorkflowSchedule />} />
            <Route path="/403" element={<AccessDenied />} />
          </Route>
        </Route>

        {/* 404 - Page Not Found */}
        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;